import type { ReportDefinitionSend, ReportDefinitionSendDelivery } from './api';
import { ReportSendError } from './report-send-error';
import { formatDateTime } from './time';

/**
 * Um envio resumido: se saiu para todos, por que falhou e de qual período é. Conta mensagens, uma por contato em
 * cada canal: 300 contatos em WhatsApp e e-mail são 600. A lista de contatos conta pessoas, por isso o rótulo diz qual.
 */
export class ReportSendOverview {
  private static readonly SHOWN_FAILURE_REASONS = 3;

  constructor(private readonly send: ReportDefinitionSend) {}

  static recipientOf(delivery: ReportDefinitionSendDelivery): string {
    return delivery.contactName ?? delivery.recipientEmail ?? 'Contato fora do HQ';
  }

  /** Quando saiu; o relatório que não saiu por canal nenhum fica pelo dia dele. */
  get when(): string {
    if (this.send.sentAt) return formatDateTime(this.send.sentAt);
    return `Relatório de ${ReportSendOverview.dayMonth(this.send.reportDate)}`;
  }

  get summary(): { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' } {
    const { reached, attempted } = this.send.tally;
    if (attempted === 0) return { label: 'Sem registro por contato', tone: 'neutral' };
    if (reached === attempted) return { label: `Saiu para todos · ${reached} de ${attempted} mensagens`, tone: 'success' };
    if (reached === 0) return { label: `Não saiu · 0 de ${attempted} mensagens`, tone: 'danger' };
    return { label: `Parcial · ${reached} de ${attempted} mensagens`, tone: 'warning' };
  }

  /** Algum contato falhou em algum canal: é o que abre a lista já filtrada nas falhas. */
  get hasFailures(): boolean {
    return this.send.tally.failureReasons.length > 0;
  }

  /** Os motivos mais comuns, já em pt-BR: dois códigos da Meta com a mesma tradução contam juntos. */
  get topFailureReasons(): { label: string; count: number }[] {
    return this.failureReasons.slice(0, ReportSendOverview.SHOWN_FAILURE_REASONS);
  }

  /** Quantas falhas ficaram nos motivos que não aparecem. */
  get otherFailures(): number {
    return this.failureReasons.slice(ReportSendOverview.SHOWN_FAILURE_REASONS).reduce((sum, reason) => sum + reason.count, 0);
  }

  private get failureReasons(): { label: string; count: number }[] {
    const counts = new Map<string, number>();
    for (const { error, count } of this.send.tally.failureReasons) {
      const label = ReportSendError.label(error ?? 'Sem motivo informado');
      counts.set(label, (counts.get(label) ?? 0) + count);
    }
    return [...counts].map(([label, count]) => ({ label, count })).sort((left, right) => right.count - left.count);
  }

  /** O período do relatório: "08/10 a 22/10"; o envio antigo sem período fica sem a linha. */
  get period(): string | null {
    if (!this.send.periodStart || !this.send.periodEnd) return null;
    return `${ReportSendOverview.dayMonth(this.send.periodStart)} a ${ReportSendOverview.dayMonth(this.send.periodEnd)}`;
  }

  private static dayMonth(isoDate: string): string {
    const [, month, day] = isoDate.split('-');
    return `${day}/${month}`;
  }
}
